import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, effect, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDivider } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { SideNavModule, VulcanIconComponent } from '@eagle-i/vulcan-component-library';
import { Store } from '@ngrx/store';
import GeoJSON from 'ol/format/GeoJSON';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { Observable } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { County } from '../../../../core/classes/county';
import { Territory } from '../../../../core/classes/territory';
import { GenPopover } from '../../../../generated/serverModels/GenPopover';
import { GenWeatherBaseline } from '../../../../generated/serverModels/GenWeatherBaseline';
import { GenWeatherPhenom } from '../../../../generated/serverModels/GenWeatherPhenom';
import { AppService } from '../../../../services/app.service';
import { MapService } from '../../../../services/map.service';
import { WeatherBaselineService } from '../../../../services/weather-baseline.service';
import { MapLayer } from '../../../../shared/classes/map-layer';
import { WeatherBaseline } from '../../../../shared/classes/weather/weather-baseline';
import { WeatherPhenomGroup } from '../../../../shared/classes/weather/weather-phenom-group';
import { MapComponent } from '../../../../shared/components/map/map.component';
import { selectCounties } from '../../../../state/location/location.reducer';
import { WeatherEventTableComponent } from './weather-event-table/weather-event-table.component';

type WeatherDataResult = {
  type: string;
  features: Array<{properties: {fipsCode: string; events?: GenWeatherBaseline[]; [key: string]: unknown}}>;
  min: number;
  max: number;
};
type Selectable<T> = T & {
  isSelected: WritableSignal<boolean>;
};
class SelectableWeatherGroup extends WeatherPhenomGroup {
  override phenoms: (Selectable<GenWeatherPhenom>)[];
  isSelected: Signal<boolean>;
  isSomeSelected: Signal<boolean>;
  constructor(group: WeatherPhenomGroup) {
    super(group);
    this.phenoms = group.phenoms.map(phenom => {
      return { ...phenom, isSelected: signal<boolean>(false) };
    });
    this.isSelected = computed(() => {
      return this.phenoms.every(p => p.isSelected());
    });
    this.isSomeSelected = computed(() => {
      return this.phenoms.some(p => p.isSelected()) && !this.isSelected();
    });
  }
}

interface WeatherSummary {
  totalEvents: number;
  eventCountMap: Map<string, number>;
  county: County;
}


@Component({
  selector: 'tgrid-weather-baseline-report',
  providers: [
    provideNativeDateAdapter(),
  ],
  imports: [
    CommonModule,
    FormsModule,
    MapComponent,
    WeatherEventTableComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    ReactiveFormsModule,
    SideNavModule,
    VulcanIconComponent,
    MatDivider,
  ],
  templateUrl: './weather-baseline-report.component.html',
  standalone: true,
  styleUrls: ['./weather-baseline-report.component.scss'],
})
export class WeatherBaselineReportComponent implements AfterViewInit {
  protected readonly mapId = 'weatherBaselineMap';
  private appService = inject(AppService);
  private weatherService = inject(WeatherBaselineService);
  private mapSource = new VectorSource();
  private store = inject(Store);

  protected maskText: Signal<string>;
  protected countyOptions: Signal<Selectable<County>[]>;
  protected filteredOptions: Signal<Selectable<County>[]>;
  protected selectedCounties: Signal<Selectable<County>[]>;
  protected focusedCounty: WritableSignal<Selectable<County> | null> = signal(null);

  protected weatherData: WritableSignal<WeatherDataResult | null> = signal(null);
  protected filteredWeatherData: Signal<WeatherBaseline[]> = signal([]);
  protected countyDataSummary: Signal<Map<string, WeatherSummary> | null>;
  protected totalWeatherSummary: Signal<WeatherSummary | null>;
  protected countySummary: Signal<Array<{ county: County, count: number; }> | null>;
  protected summaryTotal: Signal<number | null>;
  protected stateList = this.appService.selectedStates();
  protected noStates = computed(() => this.stateList.length === 0);
  protected reportName = computed(() => {
    let ret: string;
    if (this.stateList.length === 1) {
      ret = this.stateList[0].name;
    } else {
      ret = this.stateList.map(s => s.abbreviation).join(',') + ' States';
    }
    return `${ret} Weather Baseline Report`;
  });

  protected phenomGroups$: Signal<SelectableWeatherGroup[]> = toSignal(this.weatherService.getWeatherPhenomGroups().pipe(
    map(groups => {
      return groups.map(group => {
        return new SelectableWeatherGroup(group);
      });
    }),
  ), { initialValue: [] });
  protected selectedPhenoms: Signal<GenWeatherPhenom[]>;

  protected countyFilter: WritableSignal<string> = signal<string>('');
  protected onlyExtremeEvents: WritableSignal<boolean> = signal<boolean>(false);

  protected startDate: WritableSignal<Date | null> = signal<Date | null>(new Date(2018, 0, 1));
  protected endDate: WritableSignal<Date | null> = signal<Date | null>(new Date(2023, 11, 31));


  constructor() {
    this.countyOptions = toSignal(this.getCountyOptions(), { initialValue: [] });

    this.filteredOptions = computed(() => {
      const filterValue = this.countyFilter().toLowerCase();
      return this.countyOptions().filter(option => option.name.toLowerCase().includes(filterValue));
    });
    this.selectedCounties = computed(() => {
      return this.countyOptions().filter(c => c.isSelected());
    });
    this.selectedPhenoms = computed(() => {
      const selected: GenWeatherPhenom[] = [];
      this.phenomGroups$().forEach(group => {
        group.phenoms.forEach(phenom => {
          if (phenom.isSelected()) {
            selected.push(phenom);
          }
        });
      });
      return selected;
    });

    this.maskText = computed(() => {
      if (this.selectedPhenoms().length === 0) {
        return 'Select weather phenomena to see affected counties';
      }
      if (this.selectedCounties().length === 0) {
        return 'Select counties to see weather events';
      }
      if (this.weatherData() === null) {
        return 'Loading weather event data...';
      }
      return 'No weather events found for the selected criteria.';
    });


    /*
    Load Weather Data On Effect, but have a signal that updates when inputs change without reloading
    */
    effect(() => {
      const stateList = this.appService.selectedStates();
      const phenomList = this.selectedPhenoms();
      const startYear = this.startDate()?.getFullYear();
      const endYear = this.endDate()?.getFullYear();
      const onlyExtremes = this.onlyExtremeEvents();

      this.weatherData.set(null);
      if (stateList.length === 0 || phenomList.length === 0 || !startYear || !endYear) {
        return;
      } else {
        this.getWeatherData(
          stateList,
          phenomList,
          [startYear, endYear],
          onlyExtremes,
        ).subscribe(featureCollection => {
          this.weatherData.set(featureCollection);
        });
      }
    });
    this.filteredWeatherData = computed(() => {
      const weatherData = this.weatherData();
      if (!weatherData) {
        return [];
      }
      const selectedCountyGeoids = this.selectedCounties().map(c => c.geoid);
      const filteredEvents: WeatherBaseline[] = weatherData.features
        .filter(f => selectedCountyGeoids.includes(f.properties?.fipsCode))
        .flatMap(f => f.properties?.events || [])
        .map((e: GenWeatherBaseline) => new WeatherBaseline(e));

      return filteredEvents;
    });
    this.countyDataSummary = computed(() => {
      const weatherData = this.filteredWeatherData();
      if (!weatherData) {
        return null;
      }
      const phenomGroups = this.phenomGroups$();
      const categoryMap: Map<string, string> = new Map<string, string>();
      phenomGroups.forEach(group => {
        group.phenoms.forEach(phenom => {
          categoryMap.set(phenom.displayName, group.displayName);
        });
      });
      const summaryMap: Map<string, WeatherSummary> = new Map<string, WeatherSummary>();
      weatherData.forEach(event => {
        let entry = summaryMap.get(event.fipsCode);
        if (!entry) {
          entry = {
            totalEvents: 0,
            eventCountMap: new Map<string, number>(),
            county: this.countyOptions().find(c => c.geoid === event.fipsCode)!,
          };
          summaryMap.set(event.fipsCode, entry);
        }
        entry.totalEvents += 1;
        event.weatherPhenoms.forEach(phenom => {
          const groupName = categoryMap.get(phenom.displayName) || 'Other';
          const current = entry.eventCountMap.get(groupName) ?? 0;
          entry.eventCountMap.set(groupName, current + 1);
        });
      });
      return summaryMap;
    });
    this.countySummary = computed(() => {
      const countySummaryMap = this.countyDataSummary();
      if (!countySummaryMap) {
        return null;
      }
      const summaryArray = Array.from(countySummaryMap.values()).map(s => {
        return { county: s.county, count: s.totalEvents };
      });
      const sortedSummaryArray = summaryArray.sort((a, b) => b.count - a.count);
      sortedSummaryArray.length = Math.min(sortedSummaryArray.length, 3);
      return sortedSummaryArray;
    });

    this.summaryTotal = computed(() => {
      const lst = this.countySummary();
      if (!lst) {
        return null;
      }
      return lst.reduce((acc, curr) => acc + curr.count, 0);
    });

    this.totalWeatherSummary = computed(() => {
      const weatherData = this.filteredWeatherData();
      if (!weatherData) {
        return null;
      }
      const phenomGroups = this.phenomGroups$();
      const categoryMap: Map<string, string> = new Map<string, string>();
      phenomGroups.forEach(group => {
        group.phenoms.forEach(phenom => {
          categoryMap.set(phenom.displayName, group.displayName);
        });
      });
      const summary: WeatherSummary = {
        totalEvents: 0,
        eventCountMap: new Map<string, number>(),
        county: {} as County,
      };

      weatherData.forEach(event => {
        summary.totalEvents += 1;
        //multiple phenoms per event, we are only displaying their group name, but they may have multiple in the same group
        const phenomSet = new Set<string>();

        event.weatherPhenoms.forEach(phenom => {
          phenomSet.add(categoryMap.get(phenom.displayName) || 'Other');
        });
        phenomSet.forEach(groupName => {
          const current = summary.eventCountMap.get(groupName) ?? 0;
          summary.eventCountMap.set(groupName, current + 1);
        });
      });
      return summary;
    });

    /*
      Map Control
    */
    effect(() => {
      const tmpMapLayer = new MapLayer();
      tmpMapLayer.popoverData = [
        new GenPopover({ displayText: 'County name', propertyName: 'name', title: true, type: 'STRING' }),
        new GenPopover({ displayText: 'State', propertyName: 'stateName', type: 'STRING' }),
      ];
      const countyOptions = this.selectedCounties().map((c) => {
        const f = {
          type: 'Feature',
          geometry: JSON.parse(c.boundary),
          properties: {
            ...c,
            popoverData: tmpMapLayer.createPopover(c as unknown as Record<string, unknown>),
          },
        };

        return f;
      });
      const features = new GeoJSON().readFeatures({ type: 'FeatureCollection', features: countyOptions });
      this.mapSource.clear();
      this.mapSource.addFeatures(features as Feature<Geometry>[]);
    });
  }

  /*
    Event Handlers
  */
  onSelectAllCounties() {
    this.filteredOptions().forEach(c => c.isSelected.set(true));
  }
  onDeselectAllCounties() {
    this.filteredOptions().forEach(c => c.isSelected.set(false));
  }

  onPhenomToggle(phenom: Selectable<GenWeatherPhenom>) {
    phenom.isSelected.set(!phenom.isSelected());
  }
  onGroupToggle(group: SelectableWeatherGroup) {
    const newValue = !group.isSelected();
    group.phenoms.forEach(phenom => phenom.isSelected.set(newValue));
    return false;
  }

  onCountyToggle(county: Selectable<County>) {
    county.isSelected.set(!county.isSelected());
  }

  ngAfterViewInit(): void {
    const mapRef = MapService.getMapRef(this.mapId);
    if (!mapRef) {
      console.error(`Map with id ${this.mapId} not found.`);
      return;
    }
    const baseLayer = new VectorLayer({
      source: this.mapSource,
      style: new Style({
        stroke: new Stroke({
          color: 'black',
          width: 1,
        }),
        fill: new Fill({
          color: 'rgba(0, 0, 0, 0.1)',
        }),
      }),
      properties: {
        name: 'All Counties',
      },
    });

    mapRef.on('click', (evt: {pixel: number[]}) => {
      const feature = mapRef.forEachFeatureAtPixel(evt.pixel, function(feature) {
        return feature;
      });
      if (feature) {
        const countyProps = feature.getProperties() as Selectable<County>;
        const countyOption = this.countyOptions().find(c => c.geoid === countyProps.geoid) || null;
        this.focusedCounty.set(countyOption);
      }
    });
    mapRef.addLayer(baseLayer);
  }

  /*
    Data Helpers
  */

  private getCountyOptions(): Observable<Selectable<County>[]> {
    const stateIds = this.appService.selectedStates().map(s => s.id);
    return this.store.select(selectCounties).pipe(
      map((counties) => {
        const filteredCounties = stateIds.length ? counties.filter(c => stateIds.includes(c.stateId)) : counties;
        return filteredCounties;
      }),
      map((counties: County[]) => {
        return counties.map(c => {
          return {
            ...c,
            isSelected: signal<boolean>(true),
          };
        });
      }),
    );
  }
  private getWeatherData(territoriesL: Territory[], phenomsL: GenWeatherPhenom[], yearsL: number[], onlyExtremeEventsL: boolean) {
    return this.weatherService.getWeatherBaselines(
      territoriesL,
      phenomsL,
      yearsL,
      onlyExtremeEventsL,
    );
  }
}
